import { updateSardineConfig, getDeviceResponse, setupSardine } from '@sardine-ai/web-sdk'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { Cookies } from '@egw/clients'
import { v4 as uuid } from 'uuid'
import { Logger } from '@egw/logger'
import { type Config } from '../../config/config'

const log = new Logger('Risk Service')

export interface SardineSession {
  sessionKey?: string
  customerId?: string
}

const getCookieDomain = (env: string): string => {
  switch (env) {
    case 'prod':
      return '.evergreenwealth.com'
    case 'demo-prod':
      return '.evergreenwealth.com'
    case 'dev':
      return '.wealth-eg-dev.com'
    case 'demo-dev':
      return '.wealth-eg-dev.com'
    case 'local':
      return 'localhost'
    default:
      return '.wealth-eg-dev.com'
  }
}

let instance: SardineService | null = null

class SardineService {
  private config: Config | undefined
  private customerId: string | undefined
  private sessionKey: string | undefined

  constructor() {
    if (!instance) {
      instance = this
    }

    // Notificaitons listeners - on authenticated
    const authenticatedListener = (data: unknown): void => {
      const eventData = data as { detail: { userId?: string } }
      if (this.customerId !== eventData.detail.userId) {
        this.customerId = eventData.detail.userId
        // eslint-disable-next-line testing-library/no-debugging-utils
        log.debug('Sardine config updated on authenticated event: ', {
          customerId: eventData.detail.userId,
        })
        void updateSardineConfig({
          customerId: eventData.detail.userId,
          sessionKey: this.sessionKey,
          parentSessionKey: this.sessionKey,
        })
      }
    }
    notifications.on(NOTIFICATIONS.AUTH.AUTHENTICATED, authenticatedListener)

    // Notificaitons listeners - on sign out
    const signOutListener = (): void => {
      void this.resetSession()
    }
    notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, signOutListener)
  }

  init(config: Config): void {
    this.config = config
    setupSardine({
      clientId: config.sardine.clientId,
      environment: config.sardine.environment,
      apiSubdomain: config.sardine.apiSubdomain,
      pixelSubdomain: config.sardine.pixelSubdomain,
    })
    void this.resetSession()
  }

  async resetSession(): Promise<void> {
    const sessionId = uuid()
    this.sessionKey = sessionId
    Cookies.set('s-session-id', sessionId, { domain: getCookieDomain(this.config?.env ?? '*') })
    await updateSardineConfig({
      sessionKey: sessionId,
      parentSessionKey: sessionId,
      customerId: undefined,
    })
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Sardine config session updated on reset session: ', sessionId)
    const deviceResponse = getDeviceResponse()
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Device risk data: ', deviceResponse)
  }

  // Update Sardine flow if necessary
  async updateFlow(flow: string): Promise<void> {
    await updateSardineConfig({
      flow,
    })
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Sardine flow updated: ', flow)
  }
}

export const riskService = new SardineService()
